/* eslint-disable react-hooks/refs */
import { useCallback, useEffect, useRef, useState } from "react";
import { isNonNullable } from "@zyzgroup/core-common";

// const [state, setState] = useControllableState<T>({
//   value: props.value,
//   defaultValue: props.defaultValue,
//   onChange(nextValue) {
//     props.onChange?.(nextValue);
//   },
//   beforeChange: props.beforeChange
// });
// await setState((v) => ({ ...v })); // immutable

// 受控模式 “controlled” (driven by props), options.onChange 必填

// 非受控模式 “uncontrolled” (driven by state), options.defaultValue 必填

// 同时支持：受控模式 | 非受控模式
export function useControllableState<T = any>(options: {
  value: unknown;
  defaultValue?: T;
  onChange?: (value: T) => void;
  // 异步/同步拦截器：权限 / 日志 / 埋点 / 风控 / 二次确认
  beforeChange?: (nextValue: T) => boolean | Promise<boolean>;
}): readonly [
  state: T,
  setState: (newValue: T | ((prev: T) => T)) => Promise<void>
] {
  // 是否是：受控模式，完全受控于父组件
  const controlledRef = useRef(isNonNullable(options.value));
  const controlled = controlledRef.current;

  if (!controlled && !("defaultValue" in options)) {
    throw new Error("defaultValue is required in uncontrolled mode");
  }
  if (controlled && !("onChange" in options)) {
    throw new Error("onChange is required in controlled mode");
  }

  // 内部状态
  const [inner, setInner] = useState<T>(
    () => (controlled ? options.value : options.defaultValue) as T
  );

  // 对外状态
  const state = controlled ? (options.value as T) : inner;
  const stateRef = useRef(state); // 在 setState 闭包里拿到最新 state
  stateRef.current = state; // 每次 render 同步更新

  const warned = useRef(false);
  useEffect(() => {
    if (!warned.current && isNonNullable(options.value) !== controlled) {
      warned.current = true;
      // side effect
      console.warn(
        "[useControllableState] can't switch between controlled and uncontrolled mode."
      );
    }
  }, [options.value]);

  const beforeChangeRef = useRef(options.beforeChange);
  beforeChangeRef.current = options.beforeChange;
  const onChangeRef = useRef(options.onChange);
  onChangeRef.current = options.onChange;

  const version = useRef(0);
  const setState = useCallback(async (newValue: T | ((prev: T) => T)) => {
    const currentVersion = ++version.current;

    const nextValue =
      typeof newValue === "function"
        ? (newValue as (prev: T) => T)(stateRef.current)
        : newValue;

    if (beforeChangeRef.current) {
      const pass = await beforeChangeRef.current(nextValue);
      if (!pass) {
        return;
      }
    }

    if (currentVersion !== version.current) {
      return;
    }

    if (!controlled) {
      setInner(nextValue);
    }

    onChangeRef.current?.(nextValue);
  }, []);

  return [state, setState] as const;
}
